<template>
  <div class="ocr-upload-panel">
    <div class="ocr-upload-panel-head">
      <div class="ocr-upload-panel-head-icon" aria-hidden="true">
        <v-icon icon="mdi-cloud-upload-outline" size="28" />
      </div>
      <div>
        <h3 class="ocr-upload-panel-title">{{ excelMode ? "Add Excel workbooks" : "Add documents for digitization" }}</h3>
        <p class="ocr-upload-panel-lead">
          <template v-if="excelMode">
            Upload <strong>.xlsx</strong> / <strong>.xlsm</strong> into folder
            <strong class="ocr-upload-folder-name">{{ folderLabel }}</strong>, then process with the options below.
          </template>
          <template v-else>
            Upload PDFs or images into folder
            <strong class="ocr-upload-folder-name">{{ folderLabel }}</strong>.
            They appear in the queue below until you run processing.
          </template>
        </p>
      </div>
    </div>

    <div
      class="ocr-upload-dropzone"
      :class="{ 'ocr-upload-dropzone--active': dragOver, 'ocr-upload-dropzone--has-files': hasFiles }"
      role="button"
      tabindex="0"
      @click="openPicker"
      @keydown.enter.prevent="openPicker"
      @keydown.space.prevent="openPicker"
      @dragover.prevent="dragOver = true"
      @dragleave.prevent="dragOver = false"
      @drop.prevent="onDrop"
    >
      <input
        ref="nativeInput"
        type="file"
        class="ocr-upload-native-input"
        multiple
        :accept="acceptAttr"
        @change="onNativeChange"
      />
      <v-icon class="ocr-upload-dropzone-icon" icon="mdi-tray-arrow-up" size="40" />
      <p class="ocr-upload-dropzone-title">Drop files here or browse</p>
      <p class="ocr-upload-dropzone-hint">Multiple files supported · max practical size per your server limits</p>
      <div class="ocr-upload-format-chips">
        <span v-for="fmt in formats" :key="fmt" class="ocr-upload-format-chip">{{ fmt }}</span>
      </div>
      <v-btn
        variant="outlined"
        size="small"
        class="ocr-upload-browse-btn"
        @click.stop="openPicker"
      >
        Browse files
      </v-btn>
    </div>

    <ul v-if="hasFiles" class="ocr-upload-picked-list" role="list">
      <li v-for="(file, i) in fileList" :key="file.name + i" class="ocr-upload-picked-item">
        <div class="ocr-upload-picked-icon" :class="`ocr-upload-picked-icon--${kindFor(file.name)}`">
          <v-icon :icon="iconFor(file.name)" size="18" />
        </div>
        <div class="ocr-upload-picked-text">
          <span class="ocr-upload-picked-name">{{ file.name }}</span>
          <span class="ocr-upload-picked-size">{{ formatSize(file.size) }}</span>
        </div>
        <v-btn
          icon
          variant="text"
          size="x-small"
          aria-label="Remove file"
          @click.stop="removeAt(i)"
        >
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </li>
    </ul>

    <div class="ocr-upload-panel-footer">
      <div class="ocr-upload-panel-actions">
        <v-btn
          v-if="hasFiles"
          variant="text"
          size="small"
          :disabled="uploading"
          @click="clearAll"
        >
          Clear selection
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          :loading="uploading"
          :disabled="!hasFiles || uploading"
          @click="$emit('upload')"
        >
          Upload{{ hasFiles ? ` (${fileList.length})` : "" }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
const ACCEPT_EXT = [".pdf", ".png", ".jpg", ".jpeg"];
const EXCEL_EXT = [".xlsx", ".xlsm"];

export default {
  name: "OcrUploadPanel",
  props: {
    modelValue: { type: Array, default: () => [] },
    uploading: { type: Boolean, default: false },
    uploadError: { type: String, default: "" },
    excelMode: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "upload"],
  inject: {
    ocrSelectedFolder: { default: () => () => "Trial" },
  },
  data() {
    return {
      dragOver: false,
    };
  },
  computed: {
    formats() {
      return this.excelMode ? ["XLSX", "XLSM"] : ["PDF", "PNG", "JPG", "JPEG"];
    },
    acceptAttr() {
      return this.excelMode
        ? ".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : ".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg";
    },
    acceptedExt() {
      return this.excelMode ? EXCEL_EXT : ACCEPT_EXT;
    },
    folderLabel() {
      return this.ocrSelectedFolder();
    },
    fileList() {
      return Array.isArray(this.modelValue) ? this.modelValue : [];
    },
    hasFiles() {
      return this.fileList.length > 0;
    },
  },
  methods: {
    openPicker() {
      this.$refs.nativeInput?.click();
    },
    isAccepted(file) {
      const n = file.name.toLowerCase();
      return this.acceptedExt.some((ext) => n.endsWith(ext));
    },
    mergeFiles(incoming) {
      const accepted = incoming.filter((f) => this.isAccepted(f));
      const next = [...this.fileList];
      for (const f of accepted) {
        if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f);
      }
      this.$emit("update:modelValue", next);
    },
    onNativeChange(e) {
      const files = [...(e.target.files || [])];
      this.mergeFiles(files);
      e.target.value = "";
    },
    onDrop(e) {
      this.dragOver = false;
      this.mergeFiles([...e.dataTransfer.files]);
    },
    removeAt(index) {
      const next = this.fileList.filter((_, i) => i !== index);
      this.$emit("update:modelValue", next);
    },
    clearAll() {
      this.$emit("update:modelValue", []);
    },
    formatSize(bytes) {
      if (!bytes) return "—";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    kindFor(name) {
      const ext = (name.split(".").pop() || "").toLowerCase();
      if (ext === "pdf") return "pdf";
      if (["png", "jpg", "jpeg"].includes(ext)) return "image";
      return "doc";
    },
    iconFor(name) {
      const k = this.kindFor(name);
      if (k === "pdf") return "mdi-file-pdf-box";
      if (k === "image") return "mdi-file-image-outline";
      return "mdi-file-document-outline";
    },
  },
};
</script>
